import styled from 'styled-components';
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Button, Card, Dropdown, Menu, Modal, PageHeader, Radio, Select, Space, Table, Tooltip, Input, Checkbox } from "antd";
import {
  StarTwoTone,
  SmileTwoTone,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  LoginOutlined,
  DollarOutlined,
  CalendarOutlined,
  TableOutlined,
  DownOutlined,
  SwapOutlined, DiffOutlined
} from '@ant-design/icons'
import * as R from "ramda";
import moment from 'moment';
import { useNavigate } from "react-router-dom";

import { ancanaApi } from "../../api/ancanaApi";
import FormDrawer from "../../components/FormDrawer";
import HouseOwnerPicksEditor from "../../components/HouseOwnerPicksEditor";
import RecordComments from "../../components/RecordComments";
import RecordTable from "../../components/RecordTable";
import {
  BookingRoundModel,
  IBooking,
  IBookingRound,
  IWeekAssignmentRound,
  SpecialStayModel,
  WeekAssignmentRoundModel
} from "../../types/models/lodging";
import RecordSelect from "../../components/fields/RecordSelect";
import RecordDrawer from "../../components/RecordDrawer";
import useQuery from "../../hooks/useQuery";
import CustomActionButton from "../../components/CustomActionButton";
import BookingRoundPicksTable from '../../components/scheduling/BookingRoundPicksTable';
import BookingRoundCalendarView from '../../components/scheduling/BookingRoundCalendarView';
// import BookingRoundPreviewCalendar from "../../components/business/BookingRoundPreviewCalendar";
const { Title } = Typography;

const Container = styled.div`
  height: calc(100% + 40px) !important;
  overflow-y: scroll;

  .quick-actions .ant-card-body {
    gap: 8px;
    display: flex;
    flex-direction: column;
    padding: 4px 12px;
  }
  .quick-actions .ant-btn {
    width: 100%;
  }

  .ant-table-cell {
    padding: 0 !important;
  }
`;

function rotateArrayLeft(arr: any[], n: number) {
  // Calculate the actual rotation index
  const rotationIndex = n % arr.length;

  // Slice the array into two parts based on the rotation index
  const leftPart = arr.slice(rotationIndex);
  const rightPart = arr.slice(0, rotationIndex);

  // Concatenate the two parts in the desired order
  return leftPart.concat(rightPart);
}

interface IAssignment {
  id: number;
  assignment_round: number;
  unit_owner: number;
  fraction: number;
  checkin: string;
  checkout: string;
  week: number;
  preferred: boolean;
  preferred_rank: number;
  rank: number;
  price_avg: number;
  round: number;
}

function BookingRoundDetails() {
  const { round_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [bookingRound, setBookingRound] = useState<IBookingRound>();
  const [weekRanking, setWeekRanking] = useState<any[]>([]);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [fractionsCount, setFractionsCount] = useState<number>(0);
  const [houseOwners, setHouseOwners] = useState<{ id: number; fraction_numbers: number[], owner: any }[]>([]);
  const [currentOwnerEdit, setCurrentOwnerEdit] = useState<any>();
  const [assignments, setAssignments] = useState<IAssignment[]>([]);
  const [variation, setVariation] = useState("0");
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [selectedAssignments, setSelectedAssignments] = useState<IAssignment[]>([]);
  const numberOfRounds = (weekRanking.length > 0 && fractionsCount > 0) ? (weekRanking.length/fractionsCount) : 1;
  const elementRef: any = useRef(null);
  const [weekAssignmentRound, setWeekAssignmentRound] = useState<IWeekAssignmentRound>();
  const [assignmentChanges, setAssignmentChanges] = useState([]);
  const [isSwapModalVisible, setIsSwapModalVisible] = useState(false);
  const [swapReason, setSwapReason] = useState("");
  const navigate = useNavigate();
  // const [calendarVisible, setCalendarVisible] = useState<boolean>(false);
  const { query, handleFilter } = useQuery();

  const updateViewMode = React.useCallback((mode: 'table' | 'calendar') => {
    setViewMode(mode);
    handleFilter('brView', mode, true);
  }, [handleFilter]);

  const longWeekends2025 = [
    { date: '2025-01-29', holiday: 'Día de la Constitución' },
    { date: '2025-03-12', holiday: 'Natalicio de Benito Juárez' },
    { date: '2025-04-30', holiday: 'Día del Trabajo' },
    { date: '2025-09-10', holiday: 'Día de la Independencia' },
    { date: '2025-11-12', holiday: 'Día de la Revolución' }
  ];

  const findLongWeekend = (checkin: any, checkout: any) => {
    if (!checkin || !checkout) return false;

    // Convert dates to timestamp for comparison
    const start = new Date(checkin).getTime();
    const end = new Date(checkout).getTime();

    return longWeekends2025.find(holiday => {
      const holidayDate = new Date(holiday.date).getTime();
      return holidayDate >= start && holidayDate <= end;
    }) || null;
  };

  const weekNumberToString = (week: number, format = 'll', asComponent = true) => {
    if (!bookingRound) return null;
    const dateStart = moment().set('year', bookingRound.year).set('weeks', week).startOf('week').add(3, 'days');
    const dateEnd = moment().set('year', bookingRound.year).set('weeks', week).endOf('week').add(3, 'days');
    let startFormat = format;
    if (dateStart.year() === dateEnd.year()) {
      startFormat = 'MMM D, YYYY';
    }
    const dateString = `${dateStart.format(startFormat)} to ${dateEnd.format(format)}`;

    if (!asComponent) return dateString;

    return (
      <p className="pre-line" style={{ margin: 0, padding: 0, lineHeight: 1.25 }}>
        {dateString.replace(' to', ' to\n')}
      </p>
    )
  }

  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState<number[]>([]);

  const handleClose = () => {
    setIsCloseModalVisible(true);
  };

  const handleConfirmClose = () => {
    setLoading(true);
    let url = `/booking-rounds/${round_id}/close/?war=${query.get('war')}`;
    if (sendToAll) {
      url += '&send_to_all=true';
    } else if (selectedOwners.length > 0) {
      url += `&send_to=${selectedOwners.join(',')}`;
    }
    ancanaApi.get(url).then(() => window.location.reload());
  };

  const handleOpen = () => {
    setLoading(true);
    ancanaApi.get(`/booking-rounds/${round_id}/open/`).then(() => window.location.reload());
  };

  const handleSwap = () => {
    const warId = query.get('war');
    if (selectedAssignments.length !== 2 || !warId || !swapReason) return;

    setLoading(true);
    setIsSwapModalVisible(false);
    const [assignment_a, assignment_b] = selectedAssignments;

    ancanaApi.post(`/week-assignment-rounds/${warId}/swap-assignments/`, {
      assignment_a_id: assignment_a.id,
      assignment_b_id: assignment_b.id,
      reason: swapReason,
    }).then(res => {
      const newAssignments = [...assignments];
      const indexA = newAssignments.findIndex(a => a.id === res.data.assignment_a.id);
      if (indexA > -1) newAssignments[indexA] = res.data.assignment_a;
      const indexB = newAssignments.findIndex(a => a.id === res.data.assignment_b.id);
      if (indexB > -1) newAssignments[indexB] = res.data.assignment_b;
      setAssignments(newAssignments);
      setSelectedAssignments([]);
      setSwapReason("");
      ancanaApi.get(`/assignment-changes/?assignment_round=${warId}&page_size=25`).then(res => {
        setAssignmentChanges(res.data.results);
      });
    }).finally(() => setLoading(false));
  }

  useEffect(() => {
    setLoading(true);
    ancanaApi.get(`/booking-rounds/${round_id}/?expand=unit.unit_ownerships.owner,unit&fields=*,unit.id,unit.name,unit.unit_ownerships,unit.fractions,unit.weeks_available`).then(res => {
      setBookingRound(res.data);
      setFractionsCount(res.data.unit.fractions);
      setHouseOwners(res.data.unit.unit_ownerships);
      setLoading(false);
    });
    ancanaApi.get(`/bookings/?page_size=55&round=${round_id}`).then(res => {
      setBookings(res.data.results);
    });
    ancanaApi.get(`/booking-rounds/${round_id}/week-ranking/`).then(res => {
      setWeekRanking(res.data);
    });
  }, []);

  useEffect(() => {
    /*
    ancanaApi.get(`/booking-rounds/${round_id}/assignments/?variation=${variation}`).then(res => {
      setAssignments(res.data.assignments);
      setWeekRanking(res.data.ranking);
    });
     */
  }, [variation]);

  useEffect(() => {
      const warId = query.get('war');
      if (!!warId) {
        setLoading(true);
        ancanaApi.get(`/week-assignment-rounds/${warId}`).then(res => {
          setWeekAssignmentRound(res.data);
          setLoading(false);
        });
        ancanaApi.get(`/week-assignments/?assignment_round=${warId}&page_size=55`).then(res => {
          setAssignments(res.data.results);
        });
        ancanaApi.get(`/assignment-changes/?assignment_round=${warId}&page_size=25`).then(res => {
          setAssignmentChanges(res.data.results);
        });
      }
      const viewModeFromQuery = query.get('brView');
      if (viewModeFromQuery === 'table' || viewModeFromQuery === 'calendar') {
        setViewMode(viewModeFromQuery as 'table' | 'calendar');
      }
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        (document.activeElement instanceof HTMLElement && document.activeElement.isContentEditable) ||
        document.activeElement?.classList.contains('ant-select-selection-search-input')
      ) {
        return;
      }
      if (event.key === 'Escape') {
        setSelectedAssignments([]);
      } else if (event.key === '1') {
        updateViewMode('table');
      } else if (event.key === '2') {
        updateViewMode('calendar');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setSelectedAssignments, updateViewMode]);

  if (loading) return <div>Loading...</div>;

  if (!bookingRound || typeof bookingRound?.unit === 'number') return (
    <div>Booking round not found, or related unit not expanded</div>
  );

  return (
    <Container ref={elementRef} className="anti-contento contento">
      <PageHeader
        onBack={() => navigate('/booking-rounds')}
        title={`${bookingRound?.unit.name} ${bookingRound?.year} Booking Round`}
        subTitle={`${bookingRound?.owners.length} owners • ${bookingRound?.unit.weeks_available} weeks each • ${moment().day((bookingRound?.weekday_start || 0) % 7).format('dddd')} check-ins`}
        className="mb-md"
        extra={[
          <RecordSelect filter="war" autoSelectLast={!!round_id} recordModel={WeekAssignmentRoundModel} recordsetFilters={!!round_id ? { "booking_round": round_id } : {}} />,
          <Dropdown
            disabled={loading}
            overlay={() => (
              <Menu>
                {bookingRound.closed ? (
                  <Menu.Item onClick={handleOpen}>Open</Menu.Item>
                ) : (
                  <Menu.Item onClick={handleClose}>Close</Menu.Item>
                )}
              </Menu>
            )}
            key="1"
            trigger={["click"]}
          >
            <Button className="capitalize" type="primary" loading={loading}>
              <Space>{bookingRound.closed ? 'Closed' : 'Open'} <DownOutlined /></Space>
            </Button>
          </Dropdown>
        ]}
      >
      </PageHeader>
      <div className="disp-flex h-stack" style={{ alignItems: 'flex-start' }}>
        <Card
          size="small" title="Week Assigments"
          bodyStyle={{ padding: 0 }}
          style={{ flex: 1 }}
          extra={(
            <div className="disp-flex h-stack gap-xs">
              {
              /*
              <Select defaultValue="0" onSelect={e => setVariation(e)} size="small">
                <Select.Option value="0">Prioritize owners' preference</Select.Option>
                <Select.Option value="1">Prioritize price rank</Select.Option>
              </Select>
               */
              }
              <Radio.Group onChange={(e) => updateViewMode(e.target.value)} value={viewMode} size="small">
                <Tooltip title={<>Keyboard shortcut <Typography.Text keyboard>1</Typography.Text></>}>
                  <Radio.Button value="table"><TableOutlined style={{ margin: '0 24px' }} /></Radio.Button>
                </Tooltip>
                <Tooltip title={<>Keyboard shortcut <Typography.Text keyboard>2</Typography.Text></>}>
                  <Radio.Button value="calendar"><CalendarOutlined style={{ margin: '0 24px' }} /></Radio.Button>
                </Tooltip>
              </Radio.Group>
            </div>
          )}
        >
          {viewMode === 'table' ? (
            <BookingRoundPicksTable
              bookingRound={bookingRound}
              assignments={assignments as any}
              fractionsCount={fractionsCount}
              houseOwners={houseOwners}
              weekRanking={weekRanking}
              bookings={bookings}
              setCurrentOwnerEdit={setCurrentOwnerEdit}
              selectedAssignments={selectedAssignments}
              setSelectedAssignments={setSelectedAssignments}
            />
            ) : (
            <div style={{ padding: 16 }}>
              <BookingRoundCalendarView
                bookingRound={bookingRound}
                assignments={assignments as any}
                houseOwners={houseOwners}
                fractionsCount={fractionsCount}
                selectedAssignments={selectedAssignments}
                setSelectedAssignments={setSelectedAssignments}
              />
            </div>
            )}
        </Card>

        <div style={{ minWidth: 240, maxWidth: 240 }}>
          <Card size="small" title="Quick Actions" className="mb-md quick-actions">
            {
            /*
            <RecordDrawer<IWeekAssignmentRound>
              recordModel={WeekAssignmentRoundModel}
              customCreateFields={WeekAssignmentRoundModel.customCreateFields}
              recordsetFilters={{ booking_round: bookingRound?.id.toString() }}
              customRecordName="new version"
            /> */}
            {!!BookingRoundModel.customActions && BookingRoundModel.customActions?.length > 0 && (
              <CustomActionButton
                customAction={BookingRoundModel.customActions[0]}
                recordModel={BookingRoundModel}
                recordId={bookingRound?.id}
                type="primary"
              />
            )}

            {
            /*
            <Button className="w-full"><DiffOutlined />Compare versions</Button>
            */}

            <RecordTable
              recordModel={SpecialStayModel}
              modalView
              recordsetFilters={(bookingRound.usage_date_start && bookingRound.usage_date_start) ? (
                {
                  'unit': bookingRound?.unit.id,
                  'date_after': bookingRound.usage_date_start,
                  'date_before': bookingRound.usage_date_end
                }
              ) : (
                { 'unit': bookingRound?.unit.id, 'year': bookingRound?.year }
              )}
            />
            <RecordComments
              recordPath="booking-rounds"
              recordId={Number(round_id)}
              recordName={`${bookingRound?.unit.name} - ${bookingRound?.year}`}
              buttonCta="View General Comments"
            />
            <FormDrawer
              drawerWidth={550}
              actionName="View Price Ranks"
              btnType="default"
              btnIcon={<DollarOutlined />}
              recordName=""
              handleRecordAction={null}
              customFooter={<div />}
            >
              <Table
                columns={[
                  { title: 'Price Rank', dataIndex: 'rank', width: 55, render: (n) => `#${n.toLocaleString()}`, },
                  {
                    title: 'Avg. Nightly Rate',
                    dataIndex: 'avg_price',
                    width: 125,
                    render: (n) => `$${n?.toLocaleString()}`
                  },
                  {
                    title: 'Week of year',
                    dataIndex: 'week',
                    width: 85,
                    render: (week) => <Tooltip title={weekNumberToString(week)}>{week}</Tooltip>
                  },
                  {
                    title: 'Assigned',
                    dataIndex: 'week',
                    render: (week) => {
                      const assignment = assignments.find(a => a.week == week);
                      const fractionOwner = houseOwners.find(ho => ho.fraction_numbers.includes(assignment?.fraction || 0));
                      if (!assignment) {
                        return (
                          <div>
                            <CloseCircleTwoTone twoToneColor="red" />
                          </div>
                        )
                      }
                      return (
                        <div>
                          <CheckCircleTwoTone
                            twoToneColor="limegreen" /> {fractionOwner?.owner.first_name || `Empty Fraction #${assignment.fraction}`}
                        </div>
                      )
                    }
                  },
                ]}
                dataSource={weekRanking}
                pagination={false}
              />
            </FormDrawer>
          </Card>

          <Card size="small" title="Swap Weeks" className="mb-md">
            <div className="mb-sm">
              {selectedAssignments.length === 0 ? (
                "Select two weeks to swap"
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Selected Weeks:</span>
                    <Tooltip title={<>Keyboard shortcut <Typography.Text keyboard>esc</Typography.Text></>}>
                      <Button size="small" onClick={() => setSelectedAssignments([])}>Clear</Button>
                    </Tooltip>
                  </div>
                  {selectedAssignments.map(assignment => {
                    const owner = houseOwners.find(ho => ho.fraction_numbers.includes(assignment.fraction))?.owner;
                    return (
                      <div key={assignment.id}>
                        <b>{owner?.first_name || `Fraction ${assignment.fraction}`}:</b> {moment(assignment.checkin).format('MMM D')} - {moment(assignment.checkout).format('D')}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <Button
              disabled={selectedAssignments.length !== 2 || loading}
              loading={loading}
              onClick={() => setIsSwapModalVisible(true)}
            ><SwapOutlined /> Execute Swap</Button>
          </Card>

          <Card size="small" title="Recent Changes">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {assignmentChanges.map((change: any) => {
                const changeTypeColors: { [key: string]: string } = {
                  swap: '#3498db',
                  reassign: '#f1c40f',
                  add: '#2ecc71',
                  remove: '#e74c3c',
                  modify_dates: '#9b59b6',
                  algorithm_run: '#1abc9c',
                  manual_tweak: '#e67e22',
                  new_version: '#34495e',
                };
                const color = changeTypeColors[change.change_type] || '#ccc';
                return (
                  <div
                    key={change.id}
                    style={{
                      border: '1px solid rgba(150, 150, 150, 0.2)',
                      borderLeft: `5px solid ${color}`,
                      padding: '8px', borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <b style={{ textTransform: 'capitalize' }}>{change.change_type.replace('_', ' ')}</b>
                      <small>{moment(change.timestamp).format('MMM D, h:mm a')}</small>
                    </div>
                    <p style={{ margin: 0 }}>{change.reason}</p>
                    {change.change_type === 'swap' && (
                      <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.75 }}>
                        <p style={{ margin: 0 }}>
                          Fraction {change.fraction_a} ({weekNumberToString(change.week_a, 'MMM D', false)})
                          swapped with
                          Fraction {change.fraction_b} ({weekNumberToString(change.week_b, 'MMM D', false)})
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        </div>
      </div>

        <HouseOwnerPicksEditor
          onClose={() => setCurrentOwnerEdit(null)}
          visible={!!currentOwnerEdit}
          houseOwner={currentOwnerEdit}
          houseOwners={houseOwners}
          year={bookingRound?.year}
          weeksRequired={bookingRound?.weeks_required}
          assignments={assignments}
        />
        <Modal
          title="Confirm Close"
          visible={isCloseModalVisible}
          onOk={handleConfirmClose}
          onCancel={() => setIsCloseModalVisible(false)}
          confirmLoading={loading}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Checkbox checked={sendToAll} onChange={(e) => setSendToAll(e.target.checked)}>Send to all owners</Checkbox>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {houseOwners.map(owner => (
                <Checkbox
                  key={owner.id}
                  disabled={sendToAll}
                  checked={selectedOwners.includes(owner.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOwners([...selectedOwners, owner.id]);
                    } else {
                      setSelectedOwners(selectedOwners.filter(id => id !== owner.id));
                    }
                  }}
                >
                  {owner.owner.first_name} {owner.owner.last_name}
                </Checkbox>
              ))}
            </div>
          </div>
        </Modal>
      <Modal
        title="Confirm Swap"
        visible={isSwapModalVisible}
        onOk={handleSwap}
        onCancel={() => {
          setIsSwapModalVisible(false);
          setSwapReason("");
        }}
        confirmLoading={loading}
        okButtonProps={{ disabled: !swapReason }}
      >
        <p>Please provide a reason for this swap:</p>
        <Input.TextArea
          rows={4}
          value={swapReason}
          onChange={(e) => setSwapReason(e.target.value)}
          placeholder="e.g., Owner agreement"
        />
      </Modal>
        {
        /*
      <Modal
        visible={calendarVisible}
        width={900}
        onCancel={() => setCalendarVisible(false)}
        footer={null}
      >
        <div className="calendar-wrapper">
          <BookingRoundPreviewCalendar />
        </div>
      </Modal>
      */}
    </Container>
  )
}

export default BookingRoundDetails;
